import { EMPTY_TILE, assertTilesetListFits, unpackTile, withTilesetIndex } from "./packed-tile.ts";
import { readFrames, sameFrames, tilesetEntry } from "./tileset-frames.ts";
import type { LayerDocument, MapDocument, TileRef, TilesetRef, WorldDocument } from "./types.ts";

/**
 * Changes to a world's tileset list (ADR-037 amendment, P1.6-T8): entries removed and inserted by position,
 * with every packed tile cell renumbered to the list it now indexes, and what still draws from a tileset,
 * which a removal must not leave behind. An entry may be partial (ADR-129 decision 3.4): one inserted or
 * removed carries its frames, and a listed entry's frames are set in place.
 */

/**
 * One entry of a world's tileset list: where it stands, the tileset it names and, for a partial entry, its
 * frames. `frames` absent is the whole tileset.
 */
export interface TilesetEntry {
  readonly index: number;
  readonly id: string;
  readonly frames?: readonly number[];
}

/**
 * One listed entry's frames set in place: what it holds before and what it holds after, absent meaning the
 * whole tileset. The entry keeps its index, so no packed tile cell moves.
 */
export interface TilesetFrames {
  readonly id: string;
  readonly before?: readonly number[];
  readonly after?: readonly number[];
}

/**
 * Something in a document that draws from a tileset: a tile layer's cell, a prop's frame, a behaviour's
 * state frame or transition animation (ADR-053, ADR-058), or a terrain layer, which needs its terrain's
 * tileset whether or not a corner is painted.
 */
export type TilesetUse =
  | { readonly kind: "tile"; readonly tileset: string; readonly mapId: string; readonly layerId: string; readonly x: number; readonly y: number; readonly tile: number }
  | { readonly kind: "prop"; readonly tileset: string; readonly mapId: string; readonly layerId: string; readonly propId: string; readonly tile: number }
  | { readonly kind: "state"; readonly tileset: string; readonly mapId: string; readonly layerId: string; readonly propId: string; readonly tile: number; readonly state: string }
  | { readonly kind: "animation"; readonly tileset: string; readonly mapId: string; readonly layerId: string; readonly propId: string; readonly tile: number; readonly animation: string }
  | { readonly kind: "terrain"; readonly tileset: string; readonly mapId: string; readonly layerId: string; readonly terrain: string };


/**
 * The tileset a terrain id names: a terrain is `<tileset id>/<set name>`, and a tileset id never holds a
 * slash, since the importer takes it from a file name.
 *
 * @param terrainId Terrain id.
 * @returns The tileset id, or the whole id when it holds no slash.
 */
export function terrainTileset(terrainId: string): string {
  const slash = terrainId.indexOf("/");
  return slash < 0 ? terrainId : terrainId.slice(0, slash);
}

/**
 * Everything in a world that draws from a tileset, in document order: map by map and layer by layer, a
 * terrain layer once, a tile layer's first cell of each tileset in row order (later cells of the same
 * tileset in that layer add nothing a caller needs), and every prop's frame followed by its behaviour's
 * state frames and animations in key order.
 *
 * @param world The document.
 * @returns The uses.
 * @throws Error when a tile cell indexes past the world's tilesets.
 */
export function* tilesetUses(world: WorldDocument): Generator<TilesetUse> {
  for (const map of world.maps) {
    for (const layer of map.layers) {
      yield* layerUses(world.tilesets, map, layer);
    }
  }
}

/**
 * The uses of one layer, as `tilesetUses` orders them.
 *
 * @param tilesets The world's tilesets.
 * @param map The map holding the layer.
 * @param layer The layer.
 * @returns The uses.
 * @throws Error when a tile cell indexes past the world's tilesets.
 */
function* layerUses(tilesets: readonly TilesetRef[], map: MapDocument, layer: LayerDocument): Generator<TilesetUse> {
  const at = { mapId: map.id, layerId: layer.id };
  if (layer.kind === "terrain") {
    yield { kind: "terrain", tileset: terrainTileset(layer.terrain), ...at, terrain: layer.terrain };
  } else if (layer.kind === "tile") {
    const seen = new Set<number>();
    for (let i = 0; i < layer.tiles.length; i += 1) {
      const packed = layer.tiles[i] as number;
      if (packed === EMPTY_TILE) {
        continue;
      }
      const { tilesetIndex: index, tile } = unpackTile(packed);
      if (seen.has(index)) {
        continue;
      }
      seen.add(index);
      const tileset = tilesets[index];
      if (tileset === undefined) {
        throw new Error(`layer ${layer.id} cell ${i} indexes tileset ${index}, beyond the world's ${tilesets.length}`);
      }
      yield { kind: "tile", tileset: tileset.id, ...at, x: i % map.width, y: Math.floor(i / map.width), tile };
    }
  } else if (layer.kind === "prop") {
    for (const prop of layer.props) {
      const placed = { ...at, propId: prop.id };
      yield { kind: "prop", tileset: prop.frame.tileset, ...placed, tile: prop.frame.tile };
      for (const [state, frame] of sortedEntries(prop.behaviour?.frames)) {
        yield { kind: "state", tileset: frame.tileset, ...placed, tile: frame.tile, state };
      }
      for (const [animation, frame] of sortedEntries(prop.behaviour?.animations)) {
        yield { kind: "animation", tileset: frame.tileset, ...placed, tile: frame.tile, animation };
      }
    }
  }
}

/**
 * Where each frame of some tilesets is placed (ADR-129 decision 3.4): a frame is placed when a prop's frame, a
 * state frame or a transition animation names its tileset and tile. A tile cell does not place a frame, since
 * a partial entry offers no tile, so no tile layer is read. The props are read once, whatever the number of
 * tilesets asked about.
 *
 * @param world The document.
 * @param tilesets The tilesets asked about.
 * @returns By tileset, the first placement of each placed tile, in document order; a tileset nothing places
 *   has no entry.
 */
export function framePlacementsOf(world: WorldDocument, tilesets: ReadonlySet<string>): Map<string, Map<number, TilesetUse>> {
  const found = new Map<string, Map<number, TilesetUse>>();
  if (tilesets.size === 0) {
    return found;
  }
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const use of layerUses(world.tilesets, map, layer)) {
        if (use.kind === "terrain" || !tilesets.has(use.tileset)) {
          continue;
        }
        let placements = found.get(use.tileset);
        if (placements === undefined) {
          placements = new Map();
          found.set(use.tileset, placements);
        }
        if (!placements.has(use.tile)) {
          placements.set(use.tile, use);
        }
      }
    }
  }
  return found;
}

/**
 * Where each frame of one tileset is placed, as `framePlacementsOf` finds it.
 *
 * @param world The document.
 * @param tileset The tileset.
 * @returns The first placement of each placed tile, in document order.
 */
export function framePlacements(world: WorldDocument, tileset: string): Map<number, TilesetUse> {
  return framePlacementsOf(world, new Set([tileset])).get(tileset) ?? new Map();
}

/**
 * The entries of a record of tile references in key order.
 *
 * @param refs The references, or undefined.
 * @returns Key and reference pairs.
 */
function sortedEntries(refs: { readonly [key: string]: TileRef } | undefined): [string, TileRef][] {
  return refs === undefined ? [] : Object.keys(refs).sort().map((key) => [key, refs[key] as TileRef]);
}

/**
 * Says what a use is and where, for a refusal an author acts on.
 *
 * @param use The use.
 * @param withMap Whether to name the map, for worlds of several maps.
 * @returns The description.
 */
export function describeTilesetUse(use: TilesetUse, withMap = false): string {
  const where = withMap ? ` of map ${use.mapId}` : "";
  switch (use.kind) {
    case "tile":
      return `tile ${use.tileset} ${use.tile} at (${use.x}, ${use.y}) on layer ${use.layerId}${where}`;
    case "prop":
      return `prop ${use.propId} on layer ${use.layerId}${where} shows ${use.tileset} tile ${use.tile}`;
    case "state":
      return `prop ${use.propId} on layer ${use.layerId}${where} shows ${use.tileset} tile ${use.tile} in state ${use.state}`;
    case "animation":
      return `prop ${use.propId} on layer ${use.layerId}${where} plays ${use.tileset} tile ${use.tile} as ${use.animation}`;
    case "terrain":
      return `terrain layer ${use.layerId}${where} paints ${use.terrain}`;
  }
}

/**
 * Checks a list of entries is in strictly ascending index order.
 *
 * @param entries The entries.
 * @param what Which list, for the message.
 * @throws Error when two indices are out of order or equal.
 */
function assertAscending(entries: readonly TilesetEntry[], what: string): void {
  entries.forEach((entry, i) => {
    const previous = entries[i - 1];
    if (!Number.isInteger(entry.index) || entry.index < 0 || (previous !== undefined && entry.index <= previous.index)) {
      throw new Error(`${what} entries must name distinct indices in ascending order; ${entry.id} at index ${entry.index} is not`);
    }
  });
}

/**
 * Checks the frames a change sets on listed entries, each against the list as it stands.
 *
 * @param world The document.
 * @param frames The entries whose frames are set.
 * @param moved The ids the same change removes or inserts.
 * @throws Error when an id is named twice, is removed or inserted by the same change or is not listed, when
 *   its entry does not hold exactly `before`, when `after` equals `before`, or when `after` is not a frame
 *   list.
 */
function assertFrameChanges(world: WorldDocument, frames: readonly TilesetFrames[], moved: ReadonlySet<string>): void {
  const seen = new Set<string>();
  const listed = frames.length === 0 ? new Map<string, TilesetRef>() : new Map(world.tilesets.map((tileset) => [tileset.id, tileset]));
  for (const change of frames) {
    if (seen.has(change.id)) {
      throw new Error(`the frames of tileset ${change.id} are set twice`);
    }
    seen.add(change.id);
    if (moved.has(change.id)) {
      throw new Error(`tileset ${change.id} is removed or inserted by the same change that sets its frames`);
    }
    const held = listed.get(change.id);
    if (held === undefined) {
      throw new Error(`the tileset list does not hold ${change.id}`);
    }
    if (!sameFrames(held.frames, change.before)) {
      throw new Error(`tileset ${change.id} does not hold the frames the change starts from`);
    }
    if (sameFrames(change.before, change.after)) {
      throw new Error(`the frames of tileset ${change.id} are set to what they already are`);
    }
    if (change.after !== undefined) {
      readFrames(change.after, `the frames of tileset ${change.id}`);
    }
  }
}

/**
 * Removes and inserts entries of a world's tileset list, sets the frames of entries that stay, and renumbers
 * every packed tile cell of every map to the list it now indexes. Removed entries are positions in the list
 * before the change; inserted ones are positions in the list after it; an entry whose frames are set keeps its
 * place. So the change with its first two lists swapped, and each frames entry's `before` and `after` swapped,
 * is its exact inverse. A cell's tile never changes, only the index of its tileset, and a tile layer none of
 * whose cells moves keeps its grid. No cap on frames is checked here (ADR-129 decision 3.4), so an inverse
 * always applies; the caps bind where a pull is made.
 *
 * @param world The document; never mutated.
 * @param remove Entries to remove, in ascending index order, each with the frames its entry holds.
 * @param insert Entries to insert, in ascending index order.
 * @param frames Listed entries whose frames are set in place.
 * @returns The changed document, or the same document when all three lists are empty.
 * @throws Error when a removed entry is not at its index or holds other frames, an inserted id is already
 *   listed or repeated, an index is out of order or beyond the resulting list, the list would outgrow the
 *   packed form, anything still places a removed tileset, naming the first placement, an inserted entry's
 *   frames or an `after` is not a frame list, or a frames entry fails `assertFrameChanges`.
 */
export function changeTilesets(world: WorldDocument, remove: readonly TilesetEntry[], insert: readonly TilesetEntry[], frames: readonly TilesetFrames[] = []): WorldDocument {
  if (remove.length === 0 && insert.length === 0 && frames.length === 0) {
    return world;
  }
  assertAscending(remove, "removed");
  assertAscending(insert, "inserted");
  for (const entry of remove) {
    const held = world.tilesets[entry.index];
    if (held?.id !== entry.id) {
      throw new Error(`the tileset list does not hold ${entry.id} at index ${entry.index}`);
    }
    if (!sameFrames(held.frames, entry.frames)) {
      throw new Error(`the tileset list holds ${entry.id} at index ${entry.index} with other frames than the change removes`);
    }
  }
  assertFrameChanges(world, frames, new Set([...remove, ...insert].map((entry) => entry.id)));
  const removed = new Set(remove.map((entry) => entry.id));
  if (removed.size > 0) {
    for (const use of tilesetUses(world)) {
      if (removed.has(use.tileset)) {
        throw new Error(`tileset ${use.tileset} is still placed: ${describeTilesetUse(use, world.maps.length > 1)}`);
      }
    }
  }
  const removedIndices = new Set(remove.map((entry) => entry.index));
  const set = new Map(frames.map((change) => [change.id, change]));
  const kept = world.tilesets.filter((_, index) => !removedIndices.has(index)).map((tileset) => (set.has(tileset.id) ? tilesetEntry(tileset.id, set.get(tileset.id)?.after) : tileset));
  const listed = new Set(kept.map((t) => t.id));
  const length = kept.length + insert.length;
  for (const entry of insert) {
    if (listed.has(entry.id)) {
      throw new Error(`tileset ${entry.id} is already listed`);
    }
    listed.add(entry.id);
    if (entry.index >= length) {
      throw new Error(`inserted ${entry.id} at index ${entry.index}, beyond a list of ${length} entries`);
    }
    if (entry.frames !== undefined) {
      readFrames(entry.frames, `the frames of tileset ${entry.id}`);
    }
  }
  assertTilesetListFits(length, "the tileset list would hold");
  const tilesets: TilesetRef[] = [];
  let next = 0;
  let inserted = 0;
  for (let position = 0; position < length; position += 1) {
    const entry = insert[inserted];
    if (entry !== undefined && entry.index === position) {
      tilesets.push(tilesetEntry(entry.id, entry.frames));
      inserted += 1;
    } else {
      tilesets.push(kept[next] as TilesetRef);
      next += 1;
    }
  }
  const positions = new Map(tilesets.map((t, index) => [t.id, index]));
  const renumber = world.tilesets.map((t, index) => (removedIndices.has(index) ? -1 : (positions.get(t.id) as number)));
  const moves = renumber.some((to, from) => to !== from && to >= 0);
  return { ...world, tilesets, maps: moves ? world.maps.map((map) => renumberMap(map, renumber)) : world.maps };
}

/**
 * Renumbers the packed tileset index of every tile cell of a map.
 *
 * @param map The map.
 * @param renumber New index by old index; no cell holds an old index mapped to -1.
 * @returns The map, with a new grid for each tile layer a cell of which moved.
 */
function renumberMap(map: MapDocument, renumber: readonly number[]): MapDocument {
  return {
    ...map,
    layers: map.layers.map((layer) => {
      if (layer.kind !== "tile") {
        return layer;
      }
      let tiles: Uint32Array | null = null;
      for (let i = 0; i < layer.tiles.length; i += 1) {
        const packed = layer.tiles[i] as number;
        if (packed === EMPTY_TILE) {
          continue;
        }
        const from = unpackTile(packed).tilesetIndex;
        const to = renumber[from] as number;
        if (to !== from) {
          tiles ??= new Uint32Array(layer.tiles);
          tiles[i] = withTilesetIndex(packed, to);
        }
      }
      return tiles === null ? layer : { ...layer, tiles };
    }),
  };
}
